import type React from 'react';
import { observer } from 'mobx-react';
import RoleRequirement from './RoleRequirement';
import type { IRoleRequirement } from '../../../../stores/JobApplicationStore/RoleRequirement';
import { Box, Stack, Text, TextLinkButton } from 'braid-design-system';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';
import { useStores } from 'src/utils/hooks/useStores';
import { mapToSimplifiedAnswers } from './RoleRequirement/mapToSimplifiedAnswers';

interface Props {
  roleRequirements: IRoleRequirement[];
  customQuestionsCount?: number;
  isCardView?: boolean;
  onClickQuestions?: () => void;
  isMobile?: boolean;
  jobApplicationId: string;
}

interface QuestionsLinkProps {
  children: React.ReactNode;
}

const RoleRequirementsView = ({
  roleRequirements,
  customQuestionsCount = 0,
  isCardView = false,
  isMobile = false,
  onClickQuestions,
  jobApplicationId,
}: Props) => {
  const { t } = useTranslations(translations);
  const {
    jobStore: { questionnaire },
  } = useStores();
  const OverflowQuestionsLink = ({ children }: QuestionsLinkProps) => (
    <Box marginLeft={['medium', 'medium', 'none']} data-testid="questionsLink">
      <Text>
        <TextLinkButton weight="weak" onClick={onClickQuestions}>
          {children}
        </TextLinkButton>
      </Text>
    </Box>
  );
  const roleRequirementsToDisplay = isCardView
    ? roleRequirements.slice(0, 5)
    : roleRequirements;

  const roleRequirementWithSimplifiedAnswers = mapToSimplifiedAnswers({
    questionnaire,
    roleRequirements: roleRequirementsToDisplay,
  });

  return (
    <Stack space="medium">
      {roleRequirementWithSimplifiedAnswers.map((roleRequirement) => (
        <RoleRequirement
          key={roleRequirement.question}
          jobApplicationId={jobApplicationId}
          isCardView={isCardView}
          isMobile={isMobile}
          roleRequirement={roleRequirement}
        />
      ))}
      {roleRequirements.length > 5 && isCardView ? (
        <OverflowQuestionsLink>
          {t('Link to more questions', {
            numberOfQuestions: roleRequirements.length - 5,
          })}
        </OverflowQuestionsLink>
      ) : null}
      {customQuestionsCount > 0 && (
        <OverflowQuestionsLink>
          {t('Link to more custom questions', {
            numberOfQuestions: customQuestionsCount,
          })}
        </OverflowQuestionsLink>
      )}
    </Stack>
  );
};

export default observer(RoleRequirementsView);
